import type { ManualChapter } from './types'

export const appendixChapter: ManualChapter = {
  id: 'appendix',
  number: 12,
  title: 'Appendici',
  description: 'Glossario, FAQ e informazioni tecniche',
  icon: 'BookOpenIcon',
  sections: [
    {
      id: 'glossary',
      title: 'Glossario',
      content: `
## Glossario

### Termini Generali

| Termine | Definizione |
|---------|-------------|
| **Community** | L'insieme degli utenti registrati sulla piattaforma |
| **Dashboard** | Pagina principale personalizzata con widget informativi |
| **Feed** | Flusso di aggiornamenti e notifiche |
| **Widget** | Componente visivo nella dashboard |

### Forum e Discussioni

| Termine | Definizione |
|---------|-------------|
| **Thread** | Una singola discussione nel forum |
| **Post** | Un messaggio all'interno di un thread |
| **Categoria** | Raggruppamento tematico di discussioni |
| **Tag** | Etichetta per classificare i contenuti |
| **Menzione (@)** | Riferimento diretto a un utente |
| **Pin** | Thread fissato in cima alla lista |

### Formazione

| Termine | Definizione |
|---------|-------------|
| **LMS** | Learning Management System - Sistema di gestione apprendimento |
| **Corso** | Percorso formativo completo |
| **Modulo** | Sezione di un corso |
| **Lezione** | Singola unita didattica |
| **Quiz** | Test di verifica apprendimento |
| **SCORM** | Standard per contenuti e-learning |
| **Certificato** | Attestato di completamento corso |

### Task Force

| Termine | Definizione |
|---------|-------------|
| **Task Force** | Gruppo di lavoro su tema specifico |
| **Kanban** | Metodologia visiva di gestione attivita |
| **Ticket** | Singola attivita/compito da svolgere |
| **Sprint** | Periodo di lavoro intensivo |
| **Deliverable** | Risultato concreto da produrre |

### Gamification

| Termine | Definizione |
|---------|-------------|
| **Badge** | Riconoscimento virtuale per traguardi |
| **Punti** | Unita di misura dell'attivita |
| **Livello** | Grado di avanzamento nella community |
| **Classifica** | Graduatoria basata sui punti |
| **Escape Room** | Sfida di gruppo con enigmi a tempo |
| **Torneo** | Competizione su argomenti specifici |

### Eventi

| Termine | Definizione |
|---------|-------------|
| **Webinar** | Seminario online |
| **Workshop** | Sessione pratica interattiva |
| **Jitsi Meet** | Piattaforma di videoconferenza |
| **Community Day** | Evento annuale della community |
| **Talk** | Intervento/presentazione |

### Tecnici

| Termine | Definizione |
|---------|-------------|
| **SSO** | Single Sign-On - Accesso unificato |
| **2FA** | Autenticazione a due fattori |
| **API** | Interfaccia programmatica |
| **Webhook** | Notifica automatica a sistemi esterni |
`
    },
    {
      id: 'faq',
      title: 'FAQ - Domande Frequenti',
      content: `
## FAQ - Domande Frequenti

### Accesso e Account

**D: Ho dimenticato la password, come faccio?**
R: Clicca su "Password dimenticata" nella pagina di login. Riceverai un'email con il link per reimpostarla.

**D: Come cambio la mia email?**
R: Vai su Profilo → Impostazioni → Email. Dovrai confermare la nuova email.

**D: Posso avere piu account?**
R: No, e consentito un solo account per persona. Gli account duplicati verranno disattivati.

**D: Come disattivo le notifiche email?**
R: Vai su Profilo → Notifiche e deseleziona le notifiche che non desideri ricevere.

---

### Forum

**D: Come faccio a citare un messaggio?**
R: Clicca sul pulsante "Cita" sotto il messaggio che vuoi citare.

**D: Posso modificare un mio post?**
R: Si, cliccando sui tre puntini e selezionando "Modifica". C'e un limite di tempo.

**D: Come segnalo un contenuto inappropriato?**
R: Clicca sui tre puntini del messaggio e seleziona "Segnala". Scegli il motivo.

**D: Perche il mio post non appare?**
R: Potrebbe essere in coda di moderazione. Attendi l'approvazione.

---

### Corsi

**D: Posso scaricare i video dei corsi?**
R: Dipende dalle impostazioni del corso. Se il download e abilitato, vedrai l'icona apposita.

**D: Come ottengo il certificato?**
R: Completa tutte le lezioni e supera il quiz finale (se presente). Il certificato si genera automaticamente.

**D: I progressi si salvano automaticamente?**
R: Si, puoi interrompere e riprendere in qualsiasi momento.

**D: Posso rifare un quiz?**
R: Dipende dalle impostazioni. Alcuni quiz permettono tentativi multipli.

---

### Eventi

**D: Come partecipo a un evento online?**
R: Vai al dettaglio dell'evento e clicca "Partecipa" all'orario indicato. Si aprira Jitsi Meet.

**D: Posso cancellare la mia iscrizione?**
R: Si, fino all'inizio dell'evento. Vai al dettaglio e clicca "Cancella iscrizione".

**D: Dove trovo le registrazioni?**
R: Nel dettaglio dell'evento passato, sezione "Materiali".

---

### Gamification

**D: Come guadagno punti?**
R: Completando corsi, partecipando al forum, agli eventi e alle sfide.

**D: Posso nascondermi dalla classifica?**
R: Si, vai su Impostazioni → Privacy → "Mostra in classifica".

**D: I badge scadono?**
R: No, una volta ottenuti rimangono permanentemente.
`
    },
    {
      id: 'shortcuts',
      title: 'Scorciatoie da Tastiera',
      content: `
## Scorciatoie da Tastiera

### Navigazione Generale

| Scorciatoia | Azione |
|-------------|--------|
| **Alt + H** | Vai alla Home/Dashboard |
| **Alt + F** | Vai al Forum |
| **Alt + C** | Vai ai Corsi |
| **Alt + E** | Vai agli Eventi |
| **Alt + N** | Apri notifiche |
| **Alt + S** | Apri ricerca |
| **Alt + P** | Vai al Profilo |
| **Esc** | Chiudi popup/modale |

### Editor di Testo

| Scorciatoia | Azione |
|-------------|--------|
| **Ctrl + B** | Grassetto |
| **Ctrl + I** | Corsivo |
| **Ctrl + U** | Sottolineato |
| **Ctrl + K** | Inserisci link |
| **Ctrl + Z** | Annulla |
| **Ctrl + Y** | Ripristina |
| **Ctrl + Shift + V** | Incolla senza formattazione |

### Forum

| Scorciatoia | Azione |
|-------------|--------|
| **R** | Rispondi (quando leggi un thread) |
| **N** | Nuova discussione |
| **J** | Prossimo post |
| **K** | Post precedente |

### Corsi

| Scorciatoia | Azione |
|-------------|--------|
| **Spazio** | Play/Pausa video |
| **F** | Schermo intero |
| **M** | Muto/Unmuto |
| **←** | Indietro 10 secondi |
| **→** | Avanti 10 secondi |

> **Nota**: Le scorciatoie funzionano nella maggior parte dei browser moderni.
`
    },
    {
      id: 'requirements',
      title: 'Requisiti Tecnici',
      content: `
## Requisiti Tecnici

### Browser Supportati

| Browser | Versione Minima |
|---------|-----------------|
| **Google Chrome** | 90+ (consigliato) |
| **Mozilla Firefox** | 88+ |
| **Microsoft Edge** | 90+ |
| **Safari** | 14+ |

> **Nota**: Internet Explorer non e supportato.

### Connessione Internet

| Attivita | Banda Minima |
|----------|--------------|
| Navigazione | 1 Mbps |
| Video corsi | 3 Mbps |
| Videoconferenza | 5 Mbps |
| Condivisione schermo | 8 Mbps |

### Dispositivi

| Dispositivo | Supporto |
|-------------|----------|
| **Desktop/Laptop** | Completo |
| **Tablet** | Completo |
| **Smartphone** | Parziale (alcune funzioni limitate) |

### Per Videoconferenze (Jitsi Meet)

- **Webcam**: Opzionale, consigliata per interazione
- **Microfono**: Necessario per partecipare attivamente
- **Cuffie**: Consigliate per evitare eco
- **Autorizzazioni browser**: Consentire accesso a camera e microfono

### Cookie e JavaScript

- **Cookie**: Devono essere abilitati per il login
- **JavaScript**: Necessario per il funzionamento
- **LocalStorage**: Utilizzato per preferenze utente

### Firewall e Proxy

Se utilizzi un firewall aziendale, assicurati che siano consentiti:
- Dominio della piattaforma
- Porte 80 (HTTP) e 443 (HTTPS)
- WebSocket per notifiche real-time
- Server Jitsi Meet per videoconferenze
`
    },
    {
      id: 'troubleshooting',
      title: 'Risoluzione Problemi',
      content: `
## Risoluzione Problemi

### Problemi di Accesso

**Problema**: Non riesco ad accedere
**Soluzioni**:
1. Verifica di usare l'email corretta
2. Prova "Password dimenticata"
3. Controlla che l'account sia attivo
4. Svuota cache e cookie del browser
5. Prova un altro browser

**Problema**: Sessione scaduta frequentemente
**Soluzioni**:
1. Verifica che i cookie siano abilitati
2. Non usare la navigazione in incognito
3. Controlla le impostazioni di risparmio energetico

---

### Problemi Video

**Problema**: I video non si caricano
**Soluzioni**:
1. Verifica la connessione internet
2. Disabilita temporaneamente VPN
3. Prova un altro browser
4. Riduci la qualita video se disponibile

**Problema**: Video a scatti
**Soluzioni**:
1. Chiudi altre schede/applicazioni
2. Connettiti via cavo invece che WiFi
3. Abbassa la qualita del video

---

### Problemi Videoconferenza

**Problema**: Non mi sentono
**Soluzioni**:
1. Verifica che il microfono sia attivo
2. Controlla le autorizzazioni del browser
3. Seleziona il microfono corretto nelle impostazioni

**Problema**: Non vedo gli altri
**Soluzioni**:
1. Aggiorna la pagina
2. Verifica la connessione
3. Prova a uscire e rientrare

---

### Problemi Generali

**Problema**: La pagina non si carica
**Soluzioni**:
1. Ricarica la pagina (F5)
2. Svuota la cache (Ctrl+Shift+R)
3. Prova la navigazione in incognito
4. Contatta il supporto se persiste

**Problema**: Funzionalita mancanti
**Soluzioni**:
1. Verifica i tuoi permessi/ruolo
2. Alcune funzioni potrebbero essere disabilitate
3. Contatta l'amministratore
`
    },
    {
      id: 'support',
      title: 'Contatti e Supporto',
      content: `
## Contatti e Supporto

### Come Ottenere Aiuto

1. **Consulta questo manuale**: La maggior parte delle risposte e qui
2. **FAQ**: Controlla le domande frequenti
3. **Forum**: Cerca se altri hanno avuto lo stesso problema
4. **Supporto**: Contatta il team se necessario

### Canali di Supporto

| Canale | Quando usarlo |
|--------|---------------|
| **Forum Help** | Domande generali sulla piattaforma |
| **Compliance Clinic** | Quesiti tecnici agli esperti |
| **Email supporto** | Problemi tecnici con l'account |

### Informazioni da Fornire

Quando contatti il supporto, includi:
- **Descrizione** del problema
- **Passi** per riprodurlo
- **Browser** e versione utilizzati
- **Screenshot** dell'errore (se possibile)
- **Data e ora** in cui si e verificato

### Tempi di Risposta

| Priorita | Tempo indicativo |
|----------|------------------|
| Critico (accesso bloccato) | Entro 4 ore |
| Alto | Entro 1 giorno lavorativo |
| Medio | Entro 3 giorni lavorativi |
| Basso | Entro 5 giorni lavorativi |

### Orari Supporto

Il supporto tecnico e disponibile:
- Lunedi - Venerdi: 9:00 - 18:00
- Sabato, Domenica e festivi: Chiuso

Per emergenze fuori orario, utilizza il sistema di ticket con priorita "Urgente".
`
    },
    {
      id: 'changelog',
      title: 'Storico Versioni',
      content: `
## Storico Versioni

### Versione 1.3.2 (Gennaio 2026)

**Autenticazione a due fattori e bug fix.**

#### Nuove funzionalità
- Autenticazione a due fattori (2FA) completa con TOTP
- Supporto QR Code per configurazione 2FA
- Compatibilità con Google Authenticator, Authy, Microsoft Authenticator e altre app TOTP

#### Correzioni
- Fix link Privacy Policy e Termini di Servizio nella registrazione
- Fix visualizzazione nome leader nelle liste Task Force
- Fix errore chat Task Force per non membri
- Fix messaggio dopo iscrizione Task Force
- Fix leader che tenta di iscriversi alla propria Task Force
- Fix errore "Metodo Delete non consentito" per abbandono Task Force
- Fix attivazione/disattivazione 2FA dal profilo
- Fix disiscrizione corsi per ospiti

---

### Versione 1.1.2 (Dicembre 2025)

**Bug fix e miglioramenti** della piattaforma.

#### Correzioni
- Fix visualizzazione documenti per categoria
- Fix modifica eventi da pannello admin
- Fix modifica Task Force da pannello admin
- Fix visualizzazione ticket Task Force (modale invece di navigazione)
- Fix richiesta adesione Task Force
- Fix pagina "Le mie Task Force"
- Fix filtro domande per categoria in Compliance Clinic
- Fix conteggio domande per categoria
- Fix visibilita domande Compliance Clinic per utenti member
- Fix invio risposte Compliance Clinic
- Titolo pagina dinamico da impostazioni tema

#### Miglioramenti
- Centralizzazione numero versione applicazione
- Feedback visivo migliorato per azioni utente

---

### Versione 1.0.0 (Dicembre 2024)

**Release iniziale** della piattaforma CONQUIST Community Platform.

#### Funzionalita principali

- **Dashboard** personalizzabile con widget
- **Forum** con categorie, discussioni e moderazione
- **LMS** completo con corsi, quiz e certificati
- **Documenti** con repository e versionamento
- **Eventi** con calendario, prenotazioni e Jitsi Meet
- **Task Force** con Kanban, chat e documenti
- **Compliance Clinic** per consulenze con esperti
- **Gamification** con punti, badge e classifiche

#### Integrazioni

- Jitsi Meet per videoconferenze
- Sistema di notifiche real-time
- Export PDF per report e documenti

---

### Prossime Versioni (Roadmap)

Le seguenti funzionalita sono pianificate per le prossime release:

- [ ] App mobile (iOS/Android)
- [ ] Escape Room avanzate
- [ ] Tornei competitivi
- [ ] Integrazione LinkedIn per badge
- [ ] Analytics avanzati
- [ ] API pubblica

> **Nota**: La roadmap e soggetta a modifiche in base alle priorita e al feedback degli utenti.

---

### Segnalazione Bug e Suggerimenti

Per segnalare bug o proporre miglioramenti:
1. Vai alla sezione **Feedback** (se disponibile)
2. Oppure contatta il supporto
3. Descrivi dettagliatamente il problema o la proposta
`
    }
  ]
}
